import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import type { Property } from '../../../lib/supabase';
import type { User } from '../../../hooks/useAuth';
import AiMatchModal from './AiMatchModal';
import TenantWaitingModal from './TenantWaitingModal';
import LandlordOnboardingModal from '../../landlord-dashboard/components/LandlordOnboardingModal';
import RentalNoticeModal from '../../publish/components/RentalNoticeModal';
import { getHourlyFakeBase } from '../../../lib/hourlyCount';
import { useMobileTheme, type MobileThemeKey } from '../../../hooks/useMobileTheme';
import { useNavHeight } from '../../../hooks/useNavHeight';
import { trimToDistrict } from '../../../lib/locationUtils';

let _themeSyncedFromAccount = false;

interface LoggedInHomeProps { user: User; }

interface TC {
  bgImage: string; overlay: string; pageBg: string; contentBg: string;
  title: string; accent: string; sub: string; muted: string;
  badgeBg: string; badgeBorder: string; badgeText: string;
  btnPrimary: string; btnSecondary: string;
  swBg: string; swBorder: string; swActive: string; swInactive: string; swIcon: string;
  stBg: string; stBorder: string; stDivide: string; stNum: string; stLabel: string;
  card: string; cardBorder: string; cardHover: string;
  iconBg: string; iconText: string;
  assetBg: string; assetBorder: string; assetVal: string; assetLabel: string;
  assetInnerBg: string; assetFooterBg: string; assetFooterText: string;
  assetEmptyBorder: string; assetEmptyBg: string;
  cityBg: string; matchGrad: string;
  srchBg: string; srchBorder: string; srchFocus: string; srchText: string; srchCaret: string;
  price: string;
  linkBg: string; linkBorder: string; linkText: string; linkHover: string;
  section: string;
  heroCardBg: string; heroCardBorder: string;
}

const T: Record<MobileThemeKey, TC> = {
  tech: {
    bgImage: 'https://public.readdy.ai/ai/img_res/e3123554e751ebf9f43b4c8b427816b3.jpg',
    overlay: 'linear-gradient(135deg,rgba(0,0,0,.72) 0%,rgba(0,0,0,.52) 60%,rgba(0,0,0,.72) 100%)',
    pageBg: 'bg-gray-950', contentBg: 'bg-gray-950',
    title: 'text-white', accent: 'text-teal-300', sub: 'text-white/65', muted: 'text-white/40',
    badgeBg: 'bg-teal-400/20', badgeBorder: 'border-teal-300/30', badgeText: 'text-teal-100',
    btnPrimary: 'bg-teal-500 hover:bg-teal-400 text-white',
    btnSecondary: 'bg-white/15 hover:bg-white/25 text-white border border-white/30',
    swBg: 'rgba(0,0,0,.50)', swBorder: 'rgba(255,255,255,.15)',
    swActive: 'bg-white text-gray-900', swInactive: 'text-white/60 hover:text-white hover:bg-white/10',
    swIcon: 'text-white/40',
    stBg: 'rgba(255,255,255,.07)', stBorder: 'border-white/15', stDivide: 'divide-white/15',
    stNum: 'text-teal-300', stLabel: 'text-white/50',
    card: 'bg-white/8', cardBorder: 'border-white/12',
    cardHover: 'hover:bg-white/14 hover:border-teal-400/30 transition-all',
    iconBg: 'bg-teal-500', iconText: 'text-white',
    assetBg: 'bg-white/8', assetBorder: 'border-teal-400/25',
    assetVal: 'text-teal-300', assetLabel: 'text-white/55',
    assetInnerBg: 'rgba(255,241,242,.05)', assetFooterBg: 'bg-teal-500/15 hover:bg-teal-500/25',
    assetFooterText: 'text-teal-300',
    assetEmptyBorder: 'rgba(45,212,191,.25)', assetEmptyBg: 'rgba(45,212,191,.04)',
    cityBg: 'bg-teal-500', matchGrad: 'from-amber-500/90 to-orange-500/90',
    srchBg: 'bg-white/10', srchBorder: 'border-white/20', srchFocus: 'focus:border-teal-400',
    srchText: 'text-white', srchCaret: '#2dd4bf',
    price: 'text-teal-300',
    linkBg: 'bg-white/8', linkBorder: 'border-white/12', linkText: 'text-white/55',
    linkHover: 'hover:bg-white/14 hover:text-white',
    section: 'text-white/40',
    heroCardBg: 'bg-white/10', heroCardBorder: 'border-white/15',
  },
  pink: {
    bgImage: 'https://readdy.ai/api/search-image?query=ultra%20clean%20minimal%20Scandinavian%20Nordic%20apartment%20interior%20bedroom%20with%20pale%20blush%20rose%20white%20walls%2C%20soft%20dusty%20pink%20linen%20pillows%20on%20white%20bed%2C%20light%20maple%20wood%20floors%2C%20gentle%20morning%20sunlight%20streaming%20through%20sheer%20white%20curtains%2C%20very%20soft%20airy%20bright%20photography%2C%20simple%20elegant%20minimal%20furniture%20in%20pale%20neutral%20tones%2C%20professional%20real%20estate%20photography%2C%20light%20and%20airy%20aesthetic%2C%20no%20clutter%2C%20calm%20peaceful%20feminine%20minimal%20design%2C%20white%20background%20with%20very%20subtle%20warm%20pink%20accents&width=1920&height=1080&seq=desktop-hero-pink-minimal-2026&orientation=landscape',
    overlay: 'linear-gradient(135deg,rgba(254,205,211,.45) 0%,rgba(255,241,242,.30) 60%,rgba(254,205,211,.50) 100%)',
    pageBg: 'bg-rose-50', contentBg: 'bg-rose-50',
    title: 'text-gray-900', accent: 'text-rose-500', sub: 'text-rose-400', muted: 'text-rose-300',
    badgeBg: 'bg-white/85', badgeBorder: 'border-rose-200', badgeText: 'text-rose-500',
    btnPrimary: 'bg-rose-400 hover:bg-rose-500 text-white',
    btnSecondary: 'bg-white/85 hover:bg-white text-rose-500 border border-rose-200',
    swBg: 'rgba(255,255,255,.88)', swBorder: 'rgb(254,205,211)',
    swActive: 'bg-rose-400 text-white', swInactive: 'text-rose-400 hover:text-rose-600 hover:bg-rose-50',
    swIcon: 'text-rose-300',
    stBg: 'rgba(255,255,255,.82)', stBorder: 'border-rose-100', stDivide: 'divide-rose-100',
    stNum: 'text-rose-500', stLabel: 'text-rose-300',
    card: 'bg-white', cardBorder: 'border-rose-100',
    cardHover: 'hover:border-rose-200 hover:bg-rose-50/50 transition-all',
    iconBg: 'bg-rose-400', iconText: 'text-white',
    assetBg: 'bg-white', assetBorder: 'border-rose-200',
    assetVal: 'text-rose-500', assetLabel: 'text-rose-300',
    assetInnerBg: 'rgba(255,241,242,.8)', assetFooterBg: 'bg-rose-50 hover:bg-rose-100',
    assetFooterText: 'text-rose-500',
    assetEmptyBorder: 'rgb(254,205,211)', assetEmptyBg: 'rgba(255,255,255,.9)',
    cityBg: 'bg-rose-400', matchGrad: 'from-rose-400 to-rose-500',
    srchBg: 'bg-white/80', srchBorder: 'border-rose-200', srchFocus: 'focus:border-rose-400',
    srchText: 'text-gray-800', srchCaret: '#fb7185',
    price: 'text-rose-500',
    linkBg: 'bg-white', linkBorder: 'border-rose-100', linkText: 'text-rose-400',
    linkHover: 'hover:bg-white hover:border-rose-300 hover:text-rose-600',
    section: 'text-rose-300',
    heroCardBg: 'bg-white/85', heroCardBorder: 'border-rose-100',
  },
  white: {
    bgImage: 'https://readdy.ai/api/search-image?query=stunning%20modern%20apartment%20interior%20with%20floor%20to%20ceiling%20windows%20overlooking%20lush%20green%20park%2C%20abundant%20indoor%20tropical%20plants%20monstera%20fiddle%20leaf%20fig%20in%20white%20ceramic%20pots%2C%20teal%20and%20sage%20green%20accent%20cushions%20on%20light%20linen%20sofa%2C%20warm%20oak%20wood%20floors%2C%20natural%20diffused%20daylight%20streaming%20through%20sheer%20curtains%2C%20airy%20Japandi%20minimalist%20style%2C%20soft%20green%20and%20white%20tones%2C%20professional%20architectural%20photography%2C%20calm%20serene%20atmosphere%2C%20fresh%20botanical%20living%20room&width=1920&height=1080&seq=white-teal-desktop-hero-2026&orientation=landscape',
    overlay: 'linear-gradient(135deg,rgba(240,253,250,.55) 0%,rgba(255,255,255,.45) 60%,rgba(240,253,250,.55) 100%)',
    pageBg: 'bg-stone-50', contentBg: 'bg-stone-50',
    title: 'text-gray-900', accent: 'text-teal-600', sub: 'text-gray-600', muted: 'text-gray-400',
    badgeBg: 'bg-teal-50', badgeBorder: 'border-teal-200', badgeText: 'text-teal-700',
    btnPrimary: 'bg-teal-700 hover:bg-teal-800 text-white',
    btnSecondary: 'bg-white hover:bg-teal-50 text-teal-700 border-2 border-teal-200',
    swBg: 'rgba(255,255,255,.95)', swBorder: 'rgb(209,213,219)',
    swActive: 'bg-teal-700 text-white', swInactive: 'text-gray-500 hover:text-gray-800 hover:bg-gray-100',
    swIcon: 'text-gray-400',
    stBg: 'rgba(255,255,255,.95)', stBorder: 'border-gray-200', stDivide: 'divide-gray-200',
    stNum: 'text-teal-600', stLabel: 'text-gray-500',
    card: 'bg-white', cardBorder: 'border-gray-200',
    cardHover: 'hover:border-teal-200 hover:bg-teal-50/20 transition-all',
    iconBg: 'bg-teal-600', iconText: 'text-white',
    assetBg: 'bg-white', assetBorder: 'border-teal-100',
    assetVal: 'text-teal-600', assetLabel: 'text-gray-400',
    assetInnerBg: '#f0fdfa',
    assetFooterBg: 'bg-teal-50 hover:bg-teal-100',
    assetFooterText: 'text-teal-700',
    assetEmptyBorder: 'rgb(153,246,228)', assetEmptyBg: 'rgba(240,253,250,1)',
    cityBg: 'bg-teal-600', matchGrad: 'from-amber-400 to-amber-500',
    srchBg: 'bg-white', srchBorder: 'border-gray-300', srchFocus: 'focus:border-teal-400',
    srchText: 'text-gray-800', srchCaret: '#0d9488',
    price: 'text-teal-700',
    linkBg: 'bg-white', linkBorder: 'border-gray-200', linkText: 'text-gray-600',
    linkHover: 'hover:border-teal-200 hover:text-teal-600 hover:bg-teal-50/40',
    section: 'text-gray-500',
    heroCardBg: 'bg-white', heroCardBorder: 'border-teal-100',
  },
};

const THEME_OPTS: { key: MobileThemeKey; label: string; dot: string }[] = [
  { key: 'tech', label: '科技', dot: '#2dd4bf' },
  { key: 'pink', label: '粉紅', dot: '#fb7185' },
  { key: 'white', label: '白色', dot: '#d1d5db' },
];

const CITIES = [
  { city: '台北市', slug: 'taipei', icon: 'ri-building-4-line' },
  { city: '新北市', slug: 'newtaipei', icon: 'ri-building-3-line' },
  { city: '桃園市', slug: 'taoyuan', icon: 'ri-home-office-line' },
  { city: '台中市', slug: 'taichung', icon: 'ri-building-2-line' },
  { city: '台南市', slug: 'tainan', icon: 'ri-ancient-gate-line' },
  { city: '高雄市', slug: 'kaohsiung', icon: 'ri-ship-2-line' },
];

const QUICK_ACTIONS = [
  { icon: 'ri-search-2-line', label: '搜尋租屋', sub: '全台六都', href: '/regions', isMatch: false, isOnboarding: false },
  { icon: 'ri-robot-2-line', label: 'AI 配對', sub: '智能媒合', href: null, isMatch: true, isOnboarding: false },
  { icon: 'ri-home-4-line', label: '發布房源', sub: '零仲介費', href: null, isMatch: false, isOnboarding: true },
  { icon: 'ri-dashboard-3-line', label: '地產管家', sub: '資產管理', href: '/landlord-dashboard', isMatch: false, isOnboarding: false },
  { icon: 'ri-user-line', label: '我的帳號', sub: '個人設定', href: '/account', isMatch: false, isOnboarding: false },
  { icon: 'ri-robot-2-line', label: 'AI功能', sub: '功能介紹', href: '/ai-features', isMatch: false, isOnboarding: false },
];

const PLACEHOLDER_IMGS = [
  'https://readdy.ai/api/search-image?query=modern%20bright%20apartment%20interior%20with%20large%20windows%20natural%20sunlight%20clean%20minimalist%20design%20wooden%20floors%20white%20walls%20professional%20real%20estate%20photography%20simple%20background&width=600&height=400&seq=lph1&orientation=landscape',
  'https://readdy.ai/api/search-image?query=cozy%20living%20room%20apartment%20warm%20lighting%20comfortable%20furniture%20clean%20simple%20interior%20professional%20real%20estate%20photo%20neutral%20background&width=600&height=400&seq=lph2&orientation=landscape',
  'https://readdy.ai/api/search-image?query=contemporary%20apartment%20bedroom%20natural%20light%20clean%20design%20simple%20furniture%20white%20walls%20professional%20photography&width=600&height=400&seq=lph3&orientation=landscape',
];
function getPlaceholder(id: string) { return PLACEHOLDER_IMGS[id.charCodeAt(0) % PLACEHOLDER_IMGS.length]; }

interface AssetData {
  totalMarketValue: number; totalPurchasePrice: number;
  propertyGain: number; propertyCount: number; rentedCount: number;
}

const FEED_SEED = [
  { title: '大安區精裝兩房，近捷運，採光極佳', location: '台北市大安區', price: 24500, rooms: 2, size: 22, img: 'https://readdy.ai/api/search-image?query=bright%20modern%20two%20bedroom%20apartment%20interior%20natural%20light%20wood%20floors%20white%20walls%20clean%20minimalist%20Taipei%20real%20estate&width=600&height=360&seq=feed1&orientation=landscape' },
  { title: '信義區全新套房，家具全配，即可入住', location: '台北市信義區', price: 18800, rooms: 1, size: 14, img: 'https://readdy.ai/api/search-image?query=cozy%20furnished%20studio%20apartment%20modern%20interior%20Taipei%20fully%20equipped%20clean%20minimal%20design&width=600&height=360&seq=feed2&orientation=landscape' },
  { title: '中山區整層三房，零仲介費直接租', location: '台北市中山區', price: 38000, rooms: 3, size: 35, img: 'https://readdy.ai/api/search-image?query=spacious%20three%20bedroom%20apartment%20living%20room%20contemporary%20design%20bright%20airy%20open%20plan%20Taipei%20interior&width=600&height=360&seq=feed3&orientation=landscape' },
  { title: '板橋精緻兩房，鄰近捷運新埔站', location: '新北市板橋區', price: 22000, rooms: 2, size: 20, img: 'https://readdy.ai/api/search-image?query=neat%20two%20bedroom%20apartment%20near%20metro%20station%20New%20Taipei%20clean%20interior%20warm%20lighting&width=600&height=360&seq=feed4&orientation=landscape' },
  { title: '永和溫馨套房，近頂溪捷運，生活方便', location: '新北市永和區', price: 14500, rooms: 1, size: 10, img: 'https://readdy.ai/api/search-image?query=small%20cozy%20studio%20apartment%20warm%20minimal%20interior%20near%20subway%20station%20compact%20modern%20design&width=600&height=360&seq=feed5&orientation=landscape' },
  { title: '中正區文青兩房，日式裝潢，近師大', location: '台北市中正區', price: 27000, rooms: 2, size: 24, img: 'https://readdy.ai/api/search-image?query=Japanese%20style%20apartment%20interior%20two%20bedroom%20warm%20wood%20minimalist%20design%20Taipei%20cultural%20area&width=600&height=360&seq=feed6&orientation=landscape' },
  { title: '內湖科技園區附近三房，附停車位', location: '台北市內湖區', price: 42000, rooms: 3, size: 38, img: 'https://readdy.ai/api/search-image?query=large%20three%20bedroom%20apartment%20with%20parking%20modern%20interior%20tech%20district%20Taipei%20bright%20spacious&width=600&height=360&seq=feed7&orientation=landscape' },
  { title: '新莊副都心豪華兩房，全新裝潢', location: '新北市新莊區', price: 25000, rooms: 2, size: 28, img: 'https://readdy.ai/api/search-image?query=brand%20new%20luxurious%20two%20bedroom%20apartment%20full%20renovation%20modern%20design%20New%20Taipei%20bright%20interior&width=600&height=360&seq=feed8&orientation=landscape' },
];

interface FeedItem { id: string; title: string; location: string; price: number; rooms: number; size: number; agoMin: number; img: string; }

function feedRoomLabel(r: number) { return r === 1 ? '套房' : r === 2 ? '兩房' : r === 3 ? '三房' : `${r}房`; }
function feedAgoText(m: number) { if (m < 1) return '剛剛上傳'; if (m < 60) return `${m} 分鐘前上傳`; return `${Math.floor(m / 60)} 小時前上傳`; }

function MiniLatestFeed({ t, theme }: { t: TC; theme: MobileThemeKey }) {
  const navigate = useNavigate();
  const [items, setItems] = useState<FeedItem[]>([]);
  const [current, setCurrent] = useState<FeedItem | null>(null);
  const [visible, setVisible] = useState(true);
  const tickRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase
        .from('properties')
        .select('id, title, location, price, rooms, area, photos')
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(12);

      let pool: FeedItem[] = [];
      if (!error && data && data.length > 0) {
        pool = data.map((p, i) => {
          const hasPhoto = p.photos && Array.isArray(p.photos) && p.photos.length > 0;
          return {
            id: p.id,
            title: p.title || FEED_SEED[i % FEED_SEED.length].title,
            location: p.location || FEED_SEED[i % FEED_SEED.length].location,
            price: p.price || FEED_SEED[i % FEED_SEED.length].price,
            rooms: p.rooms || FEED_SEED[i % FEED_SEED.length].rooms,
            size: Number(p.area) || FEED_SEED[i % FEED_SEED.length].size,
            img: hasPhoto ? p.photos[0] : FEED_SEED[i % FEED_SEED.length].img,
            agoMin: Math.floor(Math.random() * 30) + 2,
          };
        });
      }
      if (pool.length === 0) {
        pool = FEED_SEED.map((s, i) => ({ ...s, id: `mock-${i}`, agoMin: (i + 1) * 7 }));
      }
      setItems(pool);
      setCurrent({ ...pool[0], agoMin: 2 });
      tickRef.current = 1;
    };
    load();
  }, []);

  useEffect(() => {
    if (items.length === 0) return;
    const schedule = () => {
      timerRef.current = setTimeout(() => {
        setVisible(false);
        setTimeout(() => {
          const idx = tickRef.current % items.length;
          tickRef.current += 1;
          setCurrent({ ...items[idx], agoMin: 0 });
          setVisible(true);
        }, 300);
        schedule();
      }, 5000);
    };
    schedule();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [items]);

  if (!current) return null;

  const accentColor = theme === 'pink' ? '#fb7185' : '#2dd4bf';
  const borderColor = theme === 'tech' ? 'rgba(255,255,255,.15)' : theme === 'pink' ? 'rgb(254,205,211)' : 'rgb(229,231,235)';
  const isReal = !current.id.startsWith('mock-');

  return (
    <div
      className={`w-full max-w-md mb-6 rounded-2xl overflow-hidden border transition-transform duration-150 ${isReal ? 'cursor-pointer active:scale-[0.985] hover:opacity-95' : ''}`}
      style={{ background: t.stBg, borderColor }}
      onClick={() => { if (isReal) navigate(`/property/${current.id}`); }}
      role={isReal ? 'button' : undefined}
    >
      <div className="relative w-full h-40 overflow-hidden">
        <img src={current.img} alt={current.title} className="w-full h-full object-cover object-center transition-opacity duration-300" style={{ opacity: visible ? 1 : 0 }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ background: 'rgba(0,0,0,.55)', backdropFilter: 'blur(6px)' }}>
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: accentColor }}></span>
            <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: accentColor }}></span>
          </span>
          <span className="text-[10px] font-bold text-white">最新上架</span>
        </div>
        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[9px] font-medium text-white" style={{ background: 'rgba(0,0,0,.50)' }}>
          {feedAgoText(current.agoMin)}
        </div>
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-3 transition-opacity duration-300" style={{ opacity: visible ? 1 : 0 }}>
          <p className="text-white font-bold text-sm leading-snug drop-shadow-sm line-clamp-1">{current.title}</p>
        </div>
        {isReal && (
          <div className="absolute bottom-3 right-4 flex items-center gap-1 opacity-80">
            <span className="text-[9px] text-white/80 font-medium">查看詳情</span>
            <i className="ri-arrow-right-line text-white/80 text-xs" />
          </div>
        )}
      </div>
      <div className="flex items-center justify-between px-4 py-3 transition-opacity duration-300" style={{ opacity: visible ? 1 : 0 }}>
        <div className="flex items-center gap-2 min-w-0">
          <i className={`ri-map-pin-line text-xs flex-shrink-0 ${t.accent}`}></i>
          <span className={`text-xs ${t.sub} truncate`}>{trimToDistrict(current.location)}</span>
          <span className={`text-[10px] ${t.muted} flex-shrink-0`}>· {feedRoomLabel(current.rooms)} / {current.size}坪</span>
        </div>
        <span className={`text-sm font-black flex-shrink-0 ml-2 ${t.stNum}`}>
          NT${current.price.toLocaleString()}<span className={`text-[10px] font-normal ${t.muted}`}>/月</span>
        </span>
      </div>
      <div className="h-0.5 w-full" style={{ background: theme === 'tech' ? 'rgba(255,255,255,.08)' : 'rgb(243,244,246)' }}>
        <div key={current.id} className="h-full rounded-full" style={{ background: accentColor, animation: 'feedProgress 5s linear forwards' }} />
      </div>
      <style>{`@keyframes feedProgress { from { width:0%; } to { width:100%; } }`}</style>
    </div>
  );
}

const RENTAL_NOTICE_KEY = 'rental_notice_agreed';

export default function LoggedInHome({ user }: LoggedInHomeProps) {
  const navigate = useNavigate();
  const { theme, setTheme } = useMobileTheme();
  const t = T[theme];
  const navHeight = useNavHeight(16);

  const [properties, setProperties] = useState<Property[]>([]);
  const [propsLoading, setPropsLoading] = useState(true);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [showWaitingModal, setShowWaitingModal] = useState(false);
  const [showRentalNotice, setShowRentalNotice] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [tenantMatchCount] = useState(getHourlyFakeBase());
  const [searchText, setSearchText] = useState('');
  const [assetData, setAssetData] = useState<AssetData | null>(null);
  const [assetLoading, setAssetLoading] = useState(true);

  useEffect(() => {
    if (_themeSyncedFromAccount) return;
    const syncFromAccount = async () => {
      _themeSyncedFromAccount = true;
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const saved = authUser?.user_metadata?.preferred_theme as MobileThemeKey | undefined;
      if (saved === 'tech' || saved === 'pink' || saved === 'white') setTheme(saved);
    };
    syncFromAccount();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetTheme = async (k: MobileThemeKey) => {
    setTheme(k);
    await supabase.auth.updateUser({ data: { preferred_theme: k } });
  };

  const handleSearch = () =>
    navigate(searchText.trim() ? `/regions?q=${encodeURIComponent(searchText.trim())}` : '/regions');

  const hasAssets = !!assetData && assetData.propertyCount > 0;

  const ThemeSwitcher = () => (
    <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-full border" style={{ background: T[theme].swBg, borderColor: T[theme].swBorder }}>
      <i className={`ri-palette-line text-xs mr-0.5 ${T[theme].swIcon}`} />
      {THEME_OPTS.map(({ key, label, dot }) => (
        <button key={key} type="button" onClick={() => handleSetTheme(key)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${theme === key ? T[theme].swActive : T[theme].swInactive}`}>
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dot }} />
          {label}
        </button>
      ))}
    </div>
  );

  const AssetCard = () => {
    // 資料還在讀取中：顯示骨架，不要閃出「尚未建立資產紀錄」
    if (assetLoading) {
      return (
        <div className="border rounded-2xl overflow-hidden animate-pulse"
          style={{ background: theme === 'tech' ? 'rgba(255,255,255,0.08)' : '#fff',
                   borderColor: theme === 'tech' ? 'rgba(255,255,255,0.08)' : theme === 'pink' ? 'rgb(254,205,211)' : 'rgb(229,231,235)' }}>
          <div className="px-5 pt-5 pb-5 space-y-3">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-xl ${theme === 'tech' ? 'bg-white/15' : 'bg-stone-100'}`} />
              <div className={`h-3 rounded-full w-24 ${theme === 'tech' ? 'bg-white/10' : 'bg-stone-100'}`} />
            </div>
            <div className={`h-8 rounded-full w-36 ${theme === 'tech' ? 'bg-white/10' : 'bg-stone-100'}`} />
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3].map(i => (
                <div key={i} className={`h-16 rounded-xl ${theme === 'tech' ? 'bg-white/8' : 'bg-stone-50'}`} />
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (hasAssets && assetData) {
      return (
        <div className="border rounded-2xl overflow-hidden"
          style={{ background: theme === 'tech' ? 'rgba(255,255,255,0.08)' : '#fff',
                   borderColor: theme === 'tech' ? 'rgba(255,255,255,0.08)' : theme === 'pink' ? 'rgb(254,205,211)' : 'rgb(229,231,235)' }}>
          <div className="px-5 pt-5 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-8 h-8 flex items-center justify-center rounded-xl ${t.iconBg}`}>
                <i className={`ri-line-chart-line text-sm ${t.iconText}`} />
              </div>
              <p className={`text-xs font-semibold ${t.assetLabel}`}>我的投資組合</p>
            </div>
            <div className="mb-4">
              <div className="flex items-end gap-2">
                <p className={`text-3xl font-black ${t.assetVal} leading-none`}>{assetData.totalMarketValue.toFixed(0)}</p>
                <span className={`text-base font-bold ${t.assetVal} mb-0.5`}>萬</span>
                {assetData.totalMarketValue > assetData.totalPurchasePrice && assetData.totalPurchasePrice > 0 && (
                  <span className="text-xs text-emerald-400 font-bold mb-1 flex items-center gap-0.5">
                    <i className="ri-arrow-up-s-line" />
                    {(((assetData.totalMarketValue - assetData.totalPurchasePrice) / assetData.totalPurchasePrice) * 100).toFixed(1)}%
                  </span>
                )}
              </div>
              <p className={`text-xs ${t.assetLabel} mt-0.5`}>投資組合估值（NT$）</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: '房產數量', value: `${assetData.propertyCount}間`, icon: 'ri-building-2-line' },
                { label: '出租中', value: `${assetData.rentedCount}間`, icon: 'ri-key-2-line' },
                { label: '房產收益', value: assetData.propertyGain !== 0 ? `${assetData.propertyGain > 0 ? '+' : ''}${assetData.propertyGain.toFixed(0)}萬` : '—', icon: 'ri-funds-line' },
              ].map(item => (
                <div key={item.label} className="rounded-xl p-2.5 border"
                  style={{ background: t.assetInnerBg, borderColor: theme === 'tech' ? 'rgba(255,255,255,0.08)' : theme === 'pink' ? 'rgb(254,205,211)' : 'rgb(243,244,246)' }}>
                  <i className={`${item.icon} text-sm ${t.accent} block mb-1`} />
                  <p className={`text-sm font-bold ${item.label === '房產收益' && assetData.propertyGain > 0 ? 'text-emerald-400' : item.label === '房產收益' && assetData.propertyGain < 0 ? 'text-red-400' : t.assetVal}`}>{item.value}</p>
                  <p className={`text-[10px] ${t.assetLabel} mt-0.5`}>{item.label}</p>
                </div>
              ))}
            </div>
          </div>
          <Link to="/landlord-dashboard"
            className={`flex items-center justify-between px-5 py-3 border-t ${t.assetFooterBg} transition-colors cursor-pointer`}
            style={{ borderTopColor: theme === 'tech' ? 'rgba(255,255,255,0.08)' : theme === 'pink' ? 'rgb(254,205,211)' : 'rgb(243,244,246)' }}>
            <span className={`text-xs font-medium ${t.assetFooterText}`}>查看完整資產報告</span>
            <i className={`ri-arrow-right-line text-sm ${t.assetFooterText}`} />
          </Link>
        </div>
      );
    }

    return (
      <div className="border-2 border-dashed rounded-2xl p-5 text-center"
        style={{ background: t.assetEmptyBg, borderColor: t.assetEmptyBorder }}>
        <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${t.iconBg} mx-auto mb-3`}>
          <i className={`ri-wallet-3-line text-xl ${t.iconText}`} />
        </div>
        <p className={`text-sm font-bold ${t.title} mb-1`}>尚未建立資產紀錄</p>
        <p className={`text-xs ${t.assetLabel} mb-4`}>加入地產管家即時追蹤市值與月收益</p>
        <Link to="/landlord-dashboard"
          className={`inline-flex items-center gap-2 px-5 py-2.5 ${t.btnPrimary} rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-all`}>
          <i className="ri-add-line" />新增資產
        </Link>
      </div>
    );
  };

  const PropCard = ({ p }: { p: Property }) => {
    const img = p.photos?.length ? p.photos[0] : getPlaceholder(p.id);
    return (
      <Link to={`/property/${p.id}`}
        className={`flex gap-3 ${t.card} rounded-2xl border ${t.cardBorder} overflow-hidden ${t.cardHover} cursor-pointer active:scale-[0.98] group`}
        style={theme === 'tech' ? { background: 'rgba(255,255,255,0.07)' } : {}}>
        <div className="w-24 h-24 lg:w-28 lg:h-28 flex-shrink-0 overflow-hidden">
          <img src={img} alt={p.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
        </div>
        <div className="flex-1 min-w-0 py-3 pr-3">
          <p className={`text-sm font-semibold ${t.title} truncate leading-tight mb-1`}>{p.title}</p>
          <p className={`text-xs ${t.sub} flex items-center gap-1 mb-2`}>
            <i className={`ri-map-pin-line ${t.accent}`} />{trimToDistrict(p.location)}
          </p>
          <div className="flex items-center justify-between">
            <span className={`text-sm font-bold ${t.price}`}>
              NT$ {p.price.toLocaleString()}<span className={`text-xs font-normal ${t.muted}`}>/月</span>
            </span>
            <span className={`text-xs ${t.muted}`}>
              {['店面', '廠房', '辦公室'].includes(p.property_type || '')
                ? `${p.property_type}・${p.area}坪・${p.bathrooms}衛`
                : `${p.rooms}房 · ${p.area}坪`}
            </span>
          </div>
        </div>
      </Link>
    );
  };

  useEffect(() => {
    const fetchAll = async () => {
      const uid = user.id;
      const [{ data: inv }, { data: rented }, { data: props }] = await Promise.all([
        supabase.from('investment_records').select('current_market_price,purchase_price').eq('user_id', uid),
        supabase.from('rented_properties').select('monthly_rent').eq('user_id', uid).eq('is_paused', false),
        supabase.from('properties').select('*').eq('status', 'active').order('created_at', { ascending: false }).limit(9),
      ]);
      if (inv !== null) {
        const totalMarket = (inv ?? []).reduce((s, r) => s + (parseFloat(r.current_market_price) || 0), 0);
        const totalPurch  = (inv ?? []).reduce((s, r) => s + (parseFloat(r.purchase_price) || 0), 0);
        setAssetData({ totalMarketValue: totalMarket, totalPurchasePrice: totalPurch, propertyGain: totalMarket - totalPurch, propertyCount: inv.length, rentedCount: rented?.length ?? 0 });
      }
      setAssetLoading(false);
      setProperties(props ?? []);
      setPropsLoading(false);
    };
    fetchAll();
  }, [user.id]);

  const handleRentOut = () => {
    if (sessionStorage.getItem(RENTAL_NOTICE_KEY)) { setShowOnboarding(true); }
    else { setShowRentalNotice(true); }
  };

  const handleNoticeAgree = () => {
    sessionStorage.setItem(RENTAL_NOTICE_KEY, '1');
    setShowRentalNotice(false);
    setShowOnboarding(true);
  };

  return (
    <div className={`min-h-screen ${t.pageBg}`}>

      {/* ══ HERO ══ */}
      <div className="relative overflow-hidden" style={{ minHeight: '100svh' }}>
        <div className="absolute inset-0">
          {theme === 'white' && (
            <div className="absolute inset-0 lg:hidden bg-gradient-to-br from-teal-50/60 via-stone-50/20 to-white" />
          )}
          {THEME_OPTS.map(({ key }) => (
            <img key={key} src={T[key].bgImage} alt=""
              className="absolute inset-0 w-full h-full object-cover object-center"
              style={{ opacity: theme === key ? 1 : 0, transition: 'opacity 0.5s ease' }}
              loading={key === 'tech' ? 'eager' : 'lazy'} />
          ))}
          <div className="absolute inset-0 transition-all duration-500" style={{ background: t.overlay }} />
        </div>

        <div className="relative z-10 h-full flex flex-col lg:flex-row items-center max-w-7xl mx-auto px-4 lg:px-8 pb-8 lg:pt-28 lg:pb-16 gap-8 lg:gap-16"
          style={{ paddingTop: navHeight }}>

          {/* 左欄 */}
          <div className="flex-1 w-full flex flex-col items-center lg:items-start text-center lg:text-left">
            <div className="mb-5 lg:mb-6"><ThemeSwitcher /></div>

            <div className="flex gap-2 w-full max-w-md mb-5">
              <div className="flex-1 relative">
                <i className={`ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-sm ${t.muted}`} />
                <input type="text" placeholder="搜尋地區、房型..."
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  className={`w-full pl-9 pr-4 py-3 ${t.srchBg} border ${t.srchBorder} rounded-xl text-sm ${t.srchText} focus:outline-none ${t.srchFocus}`}
                  style={{ caretColor: t.srchCaret }} />
              </div>
              <button onClick={handleSearch}
                className={`px-5 py-3 ${t.btnPrimary} rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap transition-all`}>
                搜尋
              </button>
            </div>

            <MiniLatestFeed t={t} theme={theme} />

            <div className="flex gap-3 w-full max-w-md mb-5 lg:mb-0">
              <button type="button" onClick={handleRentOut}
                className={`flex-1 flex items-center justify-center gap-2 py-3.5 ${t.btnPrimary} rounded-2xl font-bold text-sm cursor-pointer whitespace-nowrap transition-all hover:-translate-y-0.5`}>
                <i className="ri-home-4-line" />我要出租
              </button>
              <Link to="/regions"
                className={`flex-1 flex items-center justify-center gap-2 py-3.5 ${t.btnSecondary} rounded-2xl font-bold text-sm cursor-pointer whitespace-nowrap transition-all hover:-translate-y-0.5`}>
                <i className="ri-search-line" />我要找房
              </Link>
            </div>

            <div className="lg:hidden w-full max-w-md">
              <div className="flex items-center justify-between mb-2">
                <p className={`text-xs font-semibold ${t.section} uppercase tracking-wide`}>我的資產概覽</p>
                <Link to="/landlord-dashboard" className={`text-xs ${t.accent} flex items-center gap-1 cursor-pointer whitespace-nowrap`}>
                  地產管家 <i className="ri-arrow-right-line text-xs" />
                </Link>
              </div>
              <AssetCard />
            </div>
          </div>

          {/* 右欄（桌機） */}
          <div className="hidden lg:flex flex-col w-80 xl:w-96 flex-shrink-0 gap-4">
            <AssetCard />
            <div className="grid grid-cols-3 gap-2.5">
              {QUICK_ACTIONS.map(action => (
                action.isMatch ? (
                  <button key={action.label} type="button" onClick={() => setShowMatchModal(true)}
                    className={`flex flex-col items-center gap-2 py-3.5 px-1 ${t.heroCardBg} border ${t.heroCardBorder} rounded-2xl cursor-pointer group ${t.cardHover}`}
                    style={theme === 'tech' ? { background: 'rgba(255,255,255,.10)' } : {}}>
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${t.iconBg} group-hover:scale-110 transition-transform`}>
                      <i className={`${action.icon} ${t.iconText} text-base`} />
                    </div>
                    <p className={`text-xs font-semibold ${t.title} text-center leading-tight`}>{action.label}</p>
                  </button>
                ) : action.isOnboarding ? (
                  <button key={action.label} type="button" onClick={handleRentOut}
                    className={`flex flex-col items-center gap-2 py-3.5 px-1 ${t.heroCardBg} border ${t.heroCardBorder} rounded-2xl cursor-pointer group ${t.cardHover}`}
                    style={theme === 'tech' ? { background: 'rgba(255,255,255,.10)' } : {}}>
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${t.iconBg} group-hover:scale-110 transition-transform`}>
                      <i className={`${action.icon} ${t.iconText} text-base`} />
                    </div>
                    <p className={`text-xs font-semibold ${t.title} text-center leading-tight`}>{action.label}</p>
                  </button>
                ) : (
                  <Link key={action.label} to={action.href!}
                    className={`flex flex-col items-center gap-2 py-3.5 px-1 ${t.heroCardBg} border ${t.heroCardBorder} rounded-2xl cursor-pointer group ${t.cardHover}`}
                    style={theme === 'tech' ? { background: 'rgba(255,255,255,.10)' } : {}}>
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${t.iconBg} group-hover:scale-110 transition-transform`}>
                      <i className={`${action.icon} ${t.iconText} text-base`} />
                    </div>
                    <p className={`text-xs font-semibold ${t.title} text-center leading-tight`}>{action.label}</p>
                  </Link>
                )
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ══ CONTENT ══ */}
      <div className={`${t.contentBg} relative`}>
        <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8 pt-8 pb-12">
          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            <div className="lg:col-span-2 space-y-6">
              <section className="lg:hidden">
                <p className={`text-xs font-semibold ${t.section} uppercase tracking-wide mb-3`}>快速入口</p>
                <div className="grid grid-cols-3 gap-2.5">
                  {QUICK_ACTIONS.map(action => (
                    action.isMatch ? (
                      <button key={action.label} type="button" onClick={() => setShowMatchModal(true)}
                        className={`flex flex-col items-center gap-2 py-3.5 px-2 ${t.card} border ${t.cardBorder} rounded-2xl cursor-pointer group ${t.cardHover}`}
                        style={theme === 'tech' ? { background: 'rgba(255,255,255,.07)' } : {}}>
                        <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${t.iconBg} group-hover:scale-110 transition-transform`}>
                          <i className={`${action.icon} ${t.iconText} text-base`} />
                        </div>
                        <p className={`text-xs font-semibold ${t.title} text-center leading-tight`}>{action.label}</p>
                      </button>
                    ) : action.isOnboarding ? (
                      <button key={action.label} type="button" onClick={handleRentOut}
                        className={`flex flex-col items-center gap-2 py-3.5 px-2 ${t.card} border ${t.cardBorder} rounded-2xl cursor-pointer group ${t.cardHover}`}
                        style={theme === 'tech' ? { background: 'rgba(255,255,255,.07)' } : {}}>
                        <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${t.iconBg} group-hover:scale-110 transition-transform`}>
                          <i className={`${action.icon} ${t.iconText} text-base`} />
                        </div>
                        <p className={`text-xs font-semibold ${t.title} text-center leading-tight`}>{action.label}</p>
                      </button>
                    ) : (
                      <Link key={action.label} to={action.href!}
                        className={`flex flex-col items-center gap-2 py-3.5 px-2 ${t.card} border ${t.cardBorder} rounded-2xl cursor-pointer group ${t.cardHover}`}
                        style={theme === 'tech' ? { background: 'rgba(255,255,255,.07)' } : {}}>
                        <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${t.iconBg} group-hover:scale-110 transition-transform`}>
                          <i className={`${action.icon} ${t.iconText} text-base`} />
                        </div>
                        <p className={`text-xs font-semibold ${t.title} text-center leading-tight`}>{action.label}</p>
                      </Link>
                    )
                  ))}
                </div>
              </section>

              <section>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className={`text-xs font-semibold ${t.section} uppercase tracking-wide mb-0.5`}>最新上架房源</p>
                    <p className={`text-lg lg:text-xl font-black ${t.title}`}>探索最新房源</p>
                  </div>
                  <Link to="/regions" className={`text-xs ${t.accent} flex items-center gap-1 cursor-pointer whitespace-nowrap border ${t.cardBorder} px-3 py-1.5 rounded-full ${t.cardHover}`}
                    style={theme === 'tech' ? { background: 'rgba(255,255,255,.06)', borderColor: 'rgba(255,255,255,.12)' } : {}}>
                    查看全部 <i className="ri-arrow-right-line text-xs" />
                  </Link>
                </div>
                {propsLoading ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className={`h-28 ${t.card} rounded-2xl border ${t.cardBorder} animate-pulse`} />
                    ))}
                  </div>
                ) : properties.length === 0 ? (
                  <div className={`py-16 text-center text-xs ${t.muted} ${t.card} rounded-2xl border ${t.cardBorder}`}>目前沒有上架房源</div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {properties.map(p => <PropCard key={p.id} p={p} />)}
                  </div>
                )}
              </section>

              <section className="pb-4">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: '刊登房源須知', href: '/no-commission', icon: 'ri-file-list-3-line' },
                    { label: 'AI功能有哪些', href: '/ai-features', icon: 'ri-robot-2-line' },
                    { label: '聯繫客服', href: '/contact', icon: 'ri-customer-service-2-line' },
                    { label: '服務條款', href: '/terms', icon: 'ri-article-line' },
                  ].map(link => (
                    <Link key={link.label} to={link.href}
                      className={`flex items-center gap-2.5 px-4 py-3 ${t.linkBg} border ${t.linkBorder} rounded-2xl ${t.linkHover} transition-all cursor-pointer`}>
                      <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${t.iconBg} flex-shrink-0`}>
                        <i className={`${link.icon} text-xs ${t.iconText}`} />
                      </div>
                      <span className={`text-xs font-medium ${t.linkText} whitespace-nowrap`}>{link.label}</span>
                    </Link>
                  ))}
                </div>
              </section>
            </div>

            {/* 右欄（桌機） */}
            <div className="hidden lg:flex flex-col gap-5 sticky top-24 self-start">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className={`text-xs font-semibold ${t.section} uppercase tracking-wide`}>我的資產概覽</p>
                  <Link to="/landlord-dashboard" className={`text-xs ${t.accent} flex items-center gap-1 cursor-pointer whitespace-nowrap`}>
                    管家 <i className="ri-arrow-right-line text-xs" />
                  </Link>
                </div>
                <AssetCard />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className={`text-xs font-semibold ${t.section} uppercase tracking-wide`}>依城市找房</p>
                  <Link to="/regions" className={`text-xs ${t.accent} flex items-center gap-1 cursor-pointer whitespace-nowrap`}>
                    全部 <i className="ri-arrow-right-line text-xs" />
                  </Link>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {CITIES.map(c => (
                    <Link key={c.city} to={`/regions/${c.slug}`}
                      className={`flex flex-col items-center gap-1.5 py-3 px-1 ${t.card} rounded-2xl border ${t.cardBorder} ${t.cardHover} cursor-pointer text-center`}
                      style={theme === 'tech' ? { background: 'rgba(255,255,255,.07)' } : {}}>
                      <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${t.cityBg}`}>
                        <i className={`${c.icon} text-white text-xs`} />
                      </div>
                      <span className={`text-[10px] font-semibold ${t.title} whitespace-nowrap`}>{c.city}</span>
                    </Link>
                  ))}
                </div>
              </div>

              <button type="button" onClick={() => setShowWaitingModal(true)}
                className={`w-full bg-gradient-to-r ${t.matchGrad} rounded-2xl px-4 py-4 flex items-center gap-4 cursor-pointer active:scale-[0.98] transition-all`}>
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
                  <i className="ri-group-line text-white text-xl" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-white font-bold text-sm">等待配對的租客</p>
                  <p className="text-white/80 text-xs mt-0.5">目前有 <span className="font-black text-white">{tenantMatchCount}</span> 位租客等待配對</p>
                </div>
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-right-line text-white" />
                </div>
              </button>
            </div>
          </div>

          {/* 手機：城市 + AI配對 */}
          <div className="lg:hidden mt-4 space-y-4">
            <section>
              <div className="flex items-center justify-between mb-3">
                <p className={`text-xs font-semibold ${t.section} uppercase tracking-wide`}>依城市找房</p>
                <Link to="/regions" className={`text-xs ${t.accent} flex items-center gap-1 cursor-pointer whitespace-nowrap`}>
                  全部 <i className="ri-arrow-right-line text-xs" />
                </Link>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {CITIES.map(c => (
                  <Link key={c.city} to={`/regions/${c.slug}`}
                    className={`flex flex-col items-center gap-2 py-3.5 px-2 ${t.card} rounded-2xl border ${t.cardBorder} ${t.cardHover} cursor-pointer`}
                    style={theme === 'tech' ? { background: 'rgba(255,255,255,.07)' } : {}}>
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${t.cityBg} flex-shrink-0`}>
                      <i className={`${c.icon} text-white text-base`} />
                    </div>
                    <span className={`text-xs font-semibold ${t.title} whitespace-nowrap`}>{c.city}</span>
                  </Link>
                ))}
              </div>
            </section>

            <section>
              <button type="button" onClick={() => setShowWaitingModal(true)}
                className={`w-full bg-gradient-to-r ${t.matchGrad} rounded-2xl px-5 py-4 flex items-center gap-4 cursor-pointer active:scale-[0.98] transition-all`}>
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
                  <i className="ri-group-line text-white text-xl" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-white font-bold text-sm">等待配對的租客</p>
                  <p className="text-white/80 text-xs mt-0.5">目前有 <span className="font-black text-white">{tenantMatchCount}</span> 位租客等待配對</p>
                </div>
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-right-line text-white" />
                </div>
              </button>
            </section>
          </div>
        </div>
      </div>

      <AiMatchModal isOpen={showMatchModal} onClose={() => setShowMatchModal(false)} />
      <TenantWaitingModal isOpen={showWaitingModal} onClose={() => setShowWaitingModal(false)} displayCount={tenantMatchCount} />
      {showRentalNotice && <RentalNoticeModal onAgree={handleNoticeAgree} />}
      {showOnboarding && <LandlordOnboardingModal onClose={() => setShowOnboarding(false)} />}
    </div>
  );
}
